import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { ASSET_GROUP_LIMITS } from '@/types/assetGroup';

export type AssetGroupStatus = 'PAUSED' | 'ENABLED';

interface AssetGroupBasicsStepProps {
  name: string;
  status: AssetGroupStatus;
  finalUrl: string;
  onChangeName: (name: string) => void;
  onChangeStatus: (status: AssetGroupStatus) => void;
  onChangeFinalUrl: (url: string) => void;
  errors?: {
    name?: string;
    finalUrl?: string;
  };
}

const ASSET_GROUP_STATUSES: Array<{ value: AssetGroupStatus; label: string; description: string }> = [
  {
    value: 'ENABLED',
    label: 'Enabled',
    description: 'Asset group will start serving ads immediately',
  },
  {
    value: 'PAUSED',
    label: 'Paused',
    description: 'Asset group will not serve ads until manually enabled',
  },
];

export const AssetGroupBasicsStep: React.FC<AssetGroupBasicsStepProps> = ({
  name,
  status,
  finalUrl,
  onChangeName,
  onChangeStatus,
  onChangeFinalUrl,
  errors,
}) => {
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Asset Group Basics</h3>
        <p className="text-sm text-gray-600">
          Set up the basic details for your new asset group. Asset groups are used in Performance Max campaigns to organize your creative assets.
        </p>
      </div>

      {/* Asset Group Name */}
      <div>
        <label htmlFor="asset-group-name" className="block text-sm font-medium text-gray-700 mb-2">
          Asset Group Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="asset-group-name"
          type="text"
          value={name}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g., Summer Collection"
          maxLength={ASSET_GROUP_LIMITS.name.max}
          className={cn({
            'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.name,
          })}
          aria-invalid={!!errors?.name}
          aria-describedby={errors?.name ? 'asset-group-name-error' : undefined}
        />
        {errors?.name && (
          <p id="asset-group-name-error" className="mt-1 text-sm text-red-600">
            {errors.name}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Choose a descriptive name for this asset group. {name.length}/{ASSET_GROUP_LIMITS.name.max} characters.
        </p>
      </div>

      {/* Final URL */}
      <div>
        <label htmlFor="final-url" className="block text-sm font-medium text-gray-700 mb-2">
          Final URL <span className="text-red-500">*</span>
        </label>
        <Input
          id="final-url"
          type="url"
          value={finalUrl}
          onChange={(e) => onChangeFinalUrl(e.target.value)}
          placeholder="https://example.com/landing-page"
          className={cn({
            'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.finalUrl,
          })}
          aria-invalid={!!errors?.finalUrl}
          aria-describedby={errors?.finalUrl ? 'final-url-error' : undefined}
        />
        {errors?.finalUrl && (
          <p id="final-url-error" className="mt-1 text-sm text-red-600">
            {errors.finalUrl}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          The landing page URL where users will be directed when they click on your ad.
        </p>
      </div>

      {/* Asset Group Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Initial Status <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {ASSET_GROUP_STATUSES.map((statusOption) => {
            const isSelected = status === statusOption.value;
            return (
              <label
                key={statusOption.value}
                className={cn(
                  'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all',
                  'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <input
                  type="radio"
                  name="asset-group-status"
                  value={statusOption.value}
                  checked={isSelected}
                  onChange={() => onChangeStatus(statusOption.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                  aria-describedby={`status-${statusOption.value}-description`}
                />
                <div className="ml-3 flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {statusOption.label}
                  </span>
                  <p
                    id={`status-${statusOption.value}-description`}
                    className="text-xs text-gray-500 mt-1"
                  >
                    {statusOption.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};
